import { useEffect } from "react";
import { useQueryClient, type QueryClient } from "@tanstack/react-query";
import { apiBase, type EnvName } from "./api";

const syncChannel = (env: EnvName) => `og-orm-board-${env}`;

/** SSE reasons that change advisory state (manual run lifecycle only). */
const ADVISORY_SSE_PREFIXES = ["advisory:"] as const;

export function shouldRefetchAdvisory(sseReason: string | undefined): boolean {
  if (!sseReason) {
    return false;
  }
  return ADVISORY_SSE_PREFIXES.some((prefix) => sseReason.startsWith(prefix));
}

/**
 * A section submit only touches installation risk, section tiers, and
 * conflicts — never briefs or ingest feeds. Scoping the SSE-driven refetch
 * to what the reason actually changed avoids re-deriving unrelated query
 * groups on every board event (mirrors the backend fix: compute/fetch only
 * what changed, don't blindly re-derive everything on every signal).
 */
const DASHBOARD_ONLY_SSE_PREFIXES = ["conflicts:"] as const;
const SUBMIT_SSE_PREFIXES = ["submit:"] as const;

/**
 * A tab that just performed a write already has fresh data for it (either
 * applied directly from the mutation response, or via its own local
 * refetch). The server still broadcasts that same write over SSE to every
 * tab, including the one that made it — this window keeps the originating
 * tab from redundantly re-fetching in reaction to the echo of its own action.
 */
const SELF_WRITE_SUPPRESS_MS = 4000;
const lastLocalWrite = new Map<EnvName, { reason: string; at: number }>();

export function markLocalWrite(env: EnvName, reason: string) {
  lastLocalWrite.set(env, { reason, at: Date.now() });
}

function isEchoOfLocalWrite(env: EnvName, reason: string): boolean {
  const last = lastLocalWrite.get(env);
  if (!last || last.reason !== reason) {
    return false;
  }
  return Date.now() - last.at < SELF_WRITE_SUPPRESS_MS;
}

export function refreshBoard(
  qc: QueryClient,
  env: EnvName,
  opts?: { includeAdvisory?: boolean; sseReason?: string },
) {
  const reason = opts?.sseReason;
  const dashboardOnly = reason !== undefined && DASHBOARD_ONLY_SSE_PREFIXES.some((p) => reason.startsWith(p));
  const submitReason = reason !== undefined && SUBMIT_SSE_PREFIXES.some((p) => reason.startsWith(p));
  // The section-form group is the one piece a submitting tab already
  // refetched itself (for the revision witness) — skip re-doing it when this
  // call is just the SSE echo of that same write arriving a moment later.
  const sectionFormAlreadyFresh = reason !== undefined && isEchoOfLocalWrite(env, reason);

  void qc.refetchQueries({ queryKey: ["dashboard", env] });
  if (!dashboardOnly && !sectionFormAlreadyFresh) {
    void qc.refetchQueries({ queryKey: ["section-form", env] });
  }
  if (!dashboardOnly && !submitReason) {
    void qc.refetchQueries({ queryKey: ["brief", env] });
    void qc.refetchQueries({ queryKey: ["ingest-feeds", env] });
  }

  const includeAdvisory =
    opts?.includeAdvisory === true || shouldRefetchAdvisory(reason);
  if (includeAdvisory) {
    void qc.refetchQueries({ queryKey: ["advisory", env] });
  }
}

export function notifyBoardSync(env: EnvName) {
  try {
    new BroadcastChannel(syncChannel(env)).postMessage(null);
  } catch {
    /* unsupported */
  }
}

/** SSE from server + BroadcastChannel across tabs/windows on same origin. */
export function useBoardEvents(env: EnvName) {
  const qc = useQueryClient();

  useEffect(() => {
    const refreshFromBroadcast = () => refreshBoard(qc, env);
    let bc: BroadcastChannel | undefined;
    try {
      bc = new BroadcastChannel(syncChannel(env));
      bc.onmessage = refreshFromBroadcast;
    } catch {
      /* unsupported */
    }
    const es = new EventSource(`${apiBase(env)}/events/stream`);
    es.addEventListener("refresh", (event) => {
      const reason = typeof event.data === "string" ? event.data : "";
      refreshBoard(qc, env, { sseReason: reason });
    });
    return () => {
      bc?.close();
      es.close();
    };
  }, [env, qc]);
}
